import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { CURRENT_SEASON } from '@/lib/season';

export interface ManagerStanding {
  id: string;
  user_id: string;
  season: string;
  total_picks: number;
  total_points: number;
  total_wins: number;
  total_losses: number;
  total_draws: number;
  red_cards: number;
  tdd_used: number;
  tdd_available: number;
  autopicks_made: number;
  current_rank: number | null;
  apertura_rank: number | null;
  created_at: string;
  updated_at: string;
  // Profile data
  user_name: string;
  user_email: string;
  team_name: string | null;
}

export const useManagerStandings = (season: string = CURRENT_SEASON) => {
  const { currentMembership } = useAuth();
  const leagueId = currentMembership?.league_id;

  return useQuery({
    queryKey: ['manager-standings', season, leagueId],
    queryFn: async (): Promise<ManagerStanding[]> => {
      if (!leagueId) {
        return [];
      }

      try {
        // Get all active memberships for this league (these are the managers)
        const { data: memberships, error: membershipsError } = await supabase
          .from('league_memberships')
          .select('user_id, display_name, team_name')
          .eq('league_id', leagueId)
          .eq('status', 'active');

        if (membershipsError) {
          console.error('Error fetching league memberships:', membershipsError);
          throw membershipsError;
        }

        // Initialize standings for all members if they don't exist
        // Use Promise.all for parallel initialization
        if (memberships && memberships.length > 0) {
          await Promise.all(
            memberships.map((membership) =>
              supabase.rpc('initialize_manager_standings', {
                p_user_id: membership.user_id,
                p_league_id: leagueId,
                p_season: season
              })
            )
          );
        }

        // Fetch manager standings for this league
        const { data: standings, error: standingsError } = await supabase
          .from('manager_standings')
          .select('*')
          .eq('league_id', leagueId)
          .eq('season', season)
          .order('total_points', { ascending: false });

        if (standingsError) {
          console.error('Error fetching manager standings:', standingsError);
          throw standingsError;
        }

        // Create a map of memberships for easy lookup
        const membershipMap = new Map();
        if (memberships) {
          memberships.forEach(membership => {
            membershipMap.set(membership.user_id, membership);
          });
        }

        // Transform the data by merging standings with membership data
        const transformedData = (standings || [])
          .map((standing) => {
            const membership = membershipMap.get(standing.user_id);
            return {
              ...standing,
              user_name: membership?.display_name || 'Unknown Manager',
              user_email: '', // Email not available from membership
              team_name: membership?.team_name || null,
            };
          });

        return transformedData;
      } catch (error) {
        console.error('Error in useManagerStandings:', error);
        throw error;
      }
    },
    enabled: !!leagueId, // Only run if we have a league_id
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    // No refetchInterval - standings only change when matchday results are processed
  });
};

export const useUserManagerStanding = (userId: string, season: string = CURRENT_SEASON) => {
  const { currentMembership } = useAuth();
  const leagueId = currentMembership?.league_id;

  return useQuery({
    queryKey: ['user-manager-standing', userId, season, leagueId],
    queryFn: async (): Promise<ManagerStanding | null> => {
      if (!leagueId) {
        console.warn('No league_id available, cannot fetch user standing');
        return null;
      }

      // First try to get existing standing
      const { data, error } = await supabase
        .from('manager_standings')
        .select('*')
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .eq('season', season)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user manager standing:', error);
        throw error;
      }

      // Get membership data for display name
      const { data: membership, error: membershipError } = await supabase
        .from('league_memberships')
        .select('display_name, team_name')
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .eq('status', 'active')
        .maybeSingle();

      if (membershipError) {
        console.error('Error fetching membership:', membershipError);
      }

      // If no standing exists, initialize one
      if (!data) {
        const { error: initError } = await supabase
          .rpc('initialize_manager_standings', {
            p_user_id: userId,
            p_league_id: leagueId,
            p_season: season
          });

        if (initError) {
          console.error('Error initializing manager standing:', initError);
          throw initError;
        }

        // Fetch the newly created standing
        const { data: createdStanding, error: fetchError } = await supabase
          .from('manager_standings')
          .select('*')
          .eq('user_id', userId)
          .eq('league_id', leagueId)
          .eq('season', season)
          .single();

        if (fetchError) {
          console.error('Error fetching created standing:', fetchError);
          throw fetchError;
        }

        return {
          ...createdStanding,
          user_name: membership?.display_name || 'Unknown User',
          user_email: '', // Email not available from membership
          team_name: membership?.team_name || null,
        };
      }

      return {
        ...data,
        user_name: membership?.display_name || 'Unknown User',
        user_email: '', // Email not available from membership
        team_name: membership?.team_name || null,
      };
    },
    enabled: !!userId && !!leagueId,
  });
};
